// src/components/common/KanbanColumn.tsx
import React, { ForwardedRef, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTheme } from '../../contexts/ThemeContext';
import '../../components/common/KanbanColumn.css';

interface KanbanColumnProps<T> {
  title: string;
  icon: IconDefinition;
  data: T[];
  renderItem: (item: T) => React.ReactNode;
  onDragStart?: (item: T) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>, targetColumn: string) => void | Promise<void>;
  emptyMessage?: string;
  getItemClassName?: (item: T) => string;
  showCountCircle?: boolean; // Nova prop para ativar/desativar o círculo
  totalCount?: number; // Nova prop para a contagem total
}

const KanbanColumn = <T,>(
  {
    title,
    icon,
    data,
    renderItem,
    onDragOver,
    onDrop,
    emptyMessage = 'Nenhum resultado encontrado',
    getItemClassName,
    showCountCircle, // Desestruturar a nova prop
    totalCount, // Desestruturar a nova prop
  }: KanbanColumnProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const { theme } = useTheme();

  return (
    <div
      className={`kanban-column ${theme}`}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, title.toLowerCase().replace(' ', ''))}
    >
      <h3>
        {showCountCircle && totalCount !== undefined && (
          <span className="count-circle">{totalCount}</span>
        )}
        <FontAwesomeIcon icon={icon} /> {title}
      </h3>
      <hr />
      <div className="kanban-column-content">
        <div className="kanban-cards-container">
          {data.length === 0 ? (
            <p>{emptyMessage}</p>
          ) : (
            data.map((item, index) => (
              <div
                key={index}
                className={`kanban-card-wrapper ${getItemClassName ? getItemClassName(item) : ''}`}
              >
                {renderItem(item)}
              </div>
            ))
          )}
          <div ref={ref} style={{ height: '20px' }} /> {/* Sentinela */}
        </div>
      </div>
    </div>
  );
};

export default forwardRef(KanbanColumn) as <T>(
  props: KanbanColumnProps<T> & { ref?: ForwardedRef<HTMLDivElement> }
) => React.ReactElement;
